import axios from 'axios';

import { downloadBlob } from '@qwealth/qcore';
import { REACT_APP_QWEALTH_API_V3 } from '@qwealth/qdata';

import { AppThunkDispatch } from 'data/store';
import { errorHandler } from '../../services/axiosService';
import { TaxDocument, TaxDocumentDownloadList, TaxDocumentDownloadRequest } from '../models/taxDocuments';

export const fetchTaxDocumentsByYearAndQid = async (taxYear: number, qid: string, dispatch?: AppThunkDispatch): Promise<TaxDocument[]> => {
  try {
    const response = await axios.get(`${REACT_APP_QWEALTH_API_V3}/tax/${taxYear}/${qid}`);
    return response.data;
  } catch (e) {
    if (dispatch) {
      errorHandler(dispatch, 'API Error: Failed Fetching Tax Documents')(e);
    }
    return [];
  }
};

export const fetchTaxDocumentFile = async (taxDocument: TaxDocument, dispatch?: AppThunkDispatch): Promise<void> => {
  try {
    if (!taxDocument.s3Filename) {
      throw new Error('Value Error: Missing file location for tax document.');
    }

    const response = await axios.get(`${REACT_APP_QWEALTH_API_V3}/tax/doc/${taxDocument.s3Filename}/${taxDocument.documentName}`, {
      responseType: 'blob' as const,
    });

    const blobUrl = window.URL.createObjectURL(response.data);

    const fileName = taxDocument.documentName.toLowerCase().endsWith('.pdf') ? taxDocument.documentName : `${taxDocument.documentName}.pdf`;

    downloadBlob(blobUrl, fileName);
  } catch (e) {
    if (dispatch) {
      errorHandler(dispatch, `API Error: Failed to download tax document ${taxDocument.document}`)(e);
    }
  }
};

export const fetchAllTaxDocumentFiles = async (taxDocuments: TaxDocument[], dispatch?: AppThunkDispatch): Promise<void> => {
  try {
    const taxDocumentsWithFiles = taxDocuments.filter(doc => doc.s3Filename !== null);

    if (taxDocumentsWithFiles.length == 0) {
      return;
    }

    const downloadRequestList: TaxDocumentDownloadRequest[] = taxDocumentsWithFiles.map(doc => {
      return {
        s3_url: doc.s3Filename,
        document_name: doc.documentName,
      } as TaxDocumentDownloadRequest;
    });

    const payload: TaxDocumentDownloadList = { tax_document_list: downloadRequestList };

    const response = await axios.post(`${REACT_APP_QWEALTH_API_V3}/tax`, payload, {
      responseType: 'blob' as const,
    });
    const blobUrl = window.URL.createObjectURL(response.data);
    downloadBlob(blobUrl, 'tax_files.zip');
  } catch (e) {
    if (dispatch) {
      errorHandler(dispatch, 'API Error: Failed to download tax documents.')(e);
    }
  }
};

export const uploadTaxDocument = async (taxDocument: TaxDocument, file: File, dispatch?: AppThunkDispatch): Promise<boolean> => {
  try {
    const formData = new FormData();
    const pdfFile = new File([file], file.name, { type: 'application/pdf' });
    formData.append('file', pdfFile, file.name);

    const response = await axios.post(`${REACT_APP_QWEALTH_API_V3}/tax/manual_upload/${taxDocument.documentId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return true;
  } catch (e) {
    if (dispatch) {
      errorHandler(dispatch, `API Error: Failed to download tax document ${taxDocument.document}`)(e);
    }
  }
  return false;
};

export const fetchAndDownloadTaxDocumentSummary = async (taxYear: number, QID: string, dispatch?: AppThunkDispatch): Promise<void> => {
  try {
    if (taxYear < 2024) {
      throw new Error('Value Error: Cannot access summaries prior to 2024.');
    }

    const response = await axios.get(`${REACT_APP_QWEALTH_API_V3}/tax/summary/${taxYear}/${QID}`, {
      responseType: 'blob' as const,
    });

    const blobUrl = window.URL.createObjectURL(response.data);

    downloadBlob(blobUrl, `tax_summary_${QID}_For_${taxYear}.docx`);
  } catch (e) {
    if (dispatch) {
      errorHandler(dispatch, 'API Error: Failed to download tax document summary')(e);
    }
  }
};
